import { bool, func, object, string, array } from "prop-types";
import React from "react";
import { twMerge } from "tailwind-merge";

import DealSummaryUpsellCarousels from "./DealSummaryUpsellCarousels";
import SimpleButton from "@components/shared/SimpleButton";
import Typography from "@components/shared/Typography";
import ErrorMessage from "@components/shared/ErrorMessage";
import CollapsableContainer from "@components/shared/CollapsableContainer";
import DealSummaryItem from "@components/DealSummary/DealSummaryItem";
import { Deal } from "@components/DealSummary/Deal";
import VehicleQuantityInput from "@components/VehicleQuantityInput/VehicleQuantityInput";
import VehicleQuantityInfo from "@components/VehicleQuantityInfo/VehicleQuantityInfo";
import PromoCodeForm from "components/PromoCode/PromoCodeForm";

import { useDetectDevice } from "@hooks/useDetectDevice";

import { getAssumedServiceDuration } from "@lib/getAssumedServiceDuration";
import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { useTranslation } from "@lib/i18n";
import { priceAlgorithm } from "@lib/priceAlgorithm";

import {
  useAppConfigSelector,
  useIsBasketUpsellCarouselEnabled,
  useIsPromoCodesEnabledSelector,
} from "@redux/reducers/appConfig";

import { CUSTOMIZE_TYPES } from "@shared/constants";
import { getCarImagePath } from "@shared/v2/lib/getCarImagePath";
import { getAccessoryImagePath } from "@shared/v2/lib/getAccessoryImagePath";
import { getSolJourneyConfigForJourney } from "@shared/v2/lib/getSolJourneyConfigForJourney";
import DealSummaryMinimalItem from "./DealSummaryMinimalItem";

const DEFAULT_IMAGES = {
  SERVICE: "/images/gallery/service/default.jpg",
  ACCESSORY: "/images/gallery/accessory/1676030380.jpg",
};

export const SUMMARY_COLLAPSABLE_CONTAINER = "summary-collapsable-container";

const DealSummary = ({
  deal,
  isOpen,
  onChange,
  withModifyButton,
  withPromoCodeForm,
  withUpsells,
  onModifyButtonClick,
  errorMessage,
  unavailableItems,
  isQuantityEditable,
  ...props
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useDetectDevice();
  const appConfig = useAppConfigSelector();
  const promoCodesEnabled = useIsPromoCodesEnabledSelector(
    deal.financeSimulation.journey,
    deal.businessModel
  );

  const isUpsellCarouselsEnabled = useIsBasketUpsellCarouselEnabled();

  const solJourneyConfig = getSolJourneyConfigForJourney(
    deal.financeSimulation.journey,
    deal.businessModel,
    deal.currentDeal?.configuration ?? appConfig.configuration
  );

  const areMultipleVehiclesAllowed = solJourneyConfig.multipleVehicles?.enabled;

  const dealInstance = new Deal(deal, appConfig);

  const { netPrice } = priceAlgorithm.getPackPrice(deal, appConfig);

  return (
    <CollapsableContainer
      id={`${SUMMARY_COLLAPSABLE_CONTAINER}_${deal.gitId}`}
      className={{
        bodyContainer: "flex flex-col gap-4 w-full pt-0 ",
      }}
      isOpen={isOpen}
      onChange={onChange}
      titleIndex={1}
      title={
        !isOpen && isDesktop
          ? t("progressBar.isSummaryOpen")
          : t("basket.myOrder")
      }
      {...props}
    >
      {withModifyButton && (
        <SimpleButton
          className="m-0 mt-4 p-2 pl-0 self-start"
          dataId="basket-summary-modify-button"
          onClick={onModifyButtonClick}
          withArrow
        >
          {t("general.modify")}
        </SimpleButton>
      )}
      <div
        className={twMerge(
          "flex justify-between py-2",
          !withModifyButton && " mt-4"
        )}
      >
        <Typography
          className="text-lg font-semibold"
          data-id="basket-summary-configuration-heading"
        >
          {deal.carConfiguration.preconfiguration.vehicle.label}
        </Typography>
        <Typography
          className="text-lg font-semibold"
          data-id="summary-preconfiguration-heading-price"
        >
          {dealInstance.isCashJourneySelected && dealInstance.defaultCashPrice}
        </Typography>
      </div>
      {areMultipleVehiclesAllowed && (
        <>
          <VehicleQuantityInput deal={deal} isEditable={isQuantityEditable} />
          <div className="border-t border-t-primaryOrange">
            <VehicleQuantityInfo
              maximum={solJourneyConfig.multipleVehicles.maximum}
            />
          </div>
        </>
      )}
      {errorMessage && (
        <>
          <ErrorMessage>{errorMessage}</ErrorMessage>
          {!!unavailableItems?.length && (
            <ErrorMessage>
              {unavailableItems.map(item => item.label).join(", ")}
            </ErrorMessage>
          )}
        </>
      )}
      <DealSummaryItem
        title={t(`customize.${CUSTOMIZE_TYPES.DESCRIPTION}`)}
        name={dealInstance.preconfiguration.label}
        price={dealInstance.isCashJourneySelected ? netPrice ?? null : null}
        image={getStaticAssetURL(
          getCarImagePath(dealInstance.preconfiguration.id)
        )}
        description={
          <>
            <span>{t("basket.color")}</span>
            <div
              className="inline-block size-4 rounded-full"
              style={{
                backgroundColor: `#${dealInstance.preconfiguration.colorHexa}`,
              }}
            />
          </>
        }
        type={CUSTOMIZE_TYPES.DESCRIPTION}
      />
      {withUpsells && isUpsellCarouselsEnabled ? (
        <>
          <div
            className={twMerge(
              "flex flex-col  -mt-8 duration-300 transition-all gap-4",
              (dealInstance.accessory || dealInstance.service) && "-mt-4 pb-4"
            )}
          >
            <DealSummaryMinimalItem
              isVisible={!!dealInstance.accessory}
              item={dealInstance.accessory}
              type={CUSTOMIZE_TYPES.ACCESSORY}
            />
            <DealSummaryMinimalItem
              isVisible={
                dealInstance.isCashJourneySelected && !!dealInstance.service
              }
              item={dealInstance.service}
              type={CUSTOMIZE_TYPES.SERVICE}
            />
          </div>
          <DealSummaryUpsellCarousels deal={deal} />
        </>
      ) : (
        <>
          {dealInstance.accessory && (
            <DealSummaryItem
              title={t(`customize.${CUSTOMIZE_TYPES.ACCESSORY}`)}
              name={dealInstance.accessory.label}
              price={
                dealInstance.isCashJourneySelected
                  ? dealInstance.accessory?.netPrice ?? null
                  : null
              }
              image={getStaticAssetURL(
                getAccessoryImagePath(dealInstance.accessory.id)
              )}
              type={CUSTOMIZE_TYPES.ACCESSORY}
            />
          )}
          {dealInstance.isCashJourneySelected && dealInstance.service && (
            <DealSummaryItem
              title={t(`customize.${CUSTOMIZE_TYPES.SERVICE}`)}
              name={dealInstance.service.label}
              price={dealInstance.service?.netPrice}
              duration={
                dealInstance.service?.duration ||
                getAssumedServiceDuration(dealInstance.service.label)
              }
              image={getStaticAssetURL(DEFAULT_IMAGES.SERVICE)}
              type={CUSTOMIZE_TYPES.SERVICE}
            />
          )}
        </>
      )}
      {withPromoCodeForm && promoCodesEnabled && <PromoCodeForm deal={deal} />}
    </CollapsableContainer>
  );
};

DealSummary.propTypes = {
  deal: object.isRequired,
  isOpen: bool,
  onChange: func,
  withModifyButton: bool,
  withPromoCodeForm: bool,
  withUpsells: bool,
  onModifyButtonClick: func,
  errorMessage: string,
  unavailableItems: array,
  isQuantityEditable: bool,
};

export default DealSummary;
